import { Check, ChevronDown } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { EXPEDIENT_TIME_SLOTS } from "@/features/vehicle/constants";
import { cx } from "@/utils/cx";

type ExpedientTimeSelectProps = {
	label: string;
	value: string;
	disabled?: boolean;
	placeholder?: string;
	onChange: (value: string) => void;
};

export const ExpedientTimeSelect = ({
	label,
	value,
	disabled = false,
	placeholder = "Definir hora",
	onChange,
}: ExpedientTimeSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleOutsideClick = (event: MouseEvent) => {
			if (!containerRef.current) {
				return;
			}
			if (!containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutsideClick);
		document.addEventListener("keydown", handleEscape);

		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen]);

	const displayValue = value || placeholder;
	const selectedIndex = useMemo(
		() => EXPEDIENT_TIME_SLOTS.indexOf(value),
		[value]
	);

	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const [dropdownPosition, setDropdownPosition] = useState<{
		top: number;
		left: number;
		width: number;
	} | null>(null);

	useEffect(() => {
		if (!(isOpen && buttonRef.current)) {
			return;
		}

		const rect = buttonRef.current.getBoundingClientRect();
		setDropdownPosition({
			top: rect.bottom + 8,
			left: rect.left,
			width: rect.width,
		});
	}, [isOpen]);

	return (
		<div className="relative flex w-full flex-col gap-1" ref={containerRef}>
			<span className="text-md text-secondary">{label}</span>
			<button
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-label={`${label} ${displayValue}`}
				className={cx(
					"flex h-11 w-full items-center justify-center rounded-lg border border-primary bg-primary px-3 text-center shadow-xs transition",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
					disabled && "cursor-not-allowed bg-[#E9EAEB] text-fg-disabled",
					!disabled && "cursor-pointer",
					isOpen && "ring-2 ring-brand"
				)}
				disabled={disabled}
				onClick={() => setIsOpen((previous) => !previous)}
				ref={buttonRef}
				type="button"
			>
				<span
					className={cx(
						"truncate text-[16px] text-primary",
						!value && "text-fg-quaternary",
						disabled && "text-fg-disabled"
					)}
				>
					{displayValue}
				</span>
				<ChevronDown className="absolute right-3 size-5 text-fg-quaternary" />
			</button>

			{isOpen && dropdownPosition ? (
				<div
					className="fixed z-50 rounded-lg border border-secondary_alt bg-bg-primary p-1 shadow-lg"
					style={{
						top: `${dropdownPosition.top}px`,
						left: `${dropdownPosition.left}px`,
						width: `${dropdownPosition.width}px`,
					}}
				>
					<ul className="max-h-64 overflow-y-auto" role="listbox">
						{EXPEDIENT_TIME_SLOTS.map((option, index) => (
							<li key={option}>
								<button
									aria-selected={selectedIndex === index}
									className={cx(
										"flex w-full items-center justify-between rounded-md px-2 py-2 text-md text-primary transition",
										selectedIndex === index
											? "bg-bg-active font-medium"
											: "font-medium hover:bg-primary_hover"
									)}
									onClick={() => {
										onChange(option);
										setIsOpen(false);
									}}
									role="option"
									type="button"
								>
									{option}
									{selectedIndex === index && (
										<Check className="size-5 text-brand" />
									)}
								</button>
							</li>
						))}
					</ul>
				</div>
			) : null}
		</div>
	);
};
